"use client";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";

export type Heading = {
    id: string;
    text: string;
    level: string;
};

type HeadingGroup = {
    mainHeading: Heading;
    subHeadings: Heading[];
};

export default function TableOfContents({ headings }: { headings: Heading[] }) {
    const t = useTranslations("TableOfContents");
    const [isOpen, setIsOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});

    // Add scroll behavior
    useEffect(() => {
        // Add CSS for better scroll behavior
        const style = document.createElement('style');
        style.innerHTML = `
      .post-content h2,
      .post-content h3,
      .post-content h4,
      .post-content h5 {
        scroll-margin-top: 100px;
        padding-top: 10px;
      }
      
      @media (max-width: 768px) {
        .post-content h2,
        .post-content h3,
        .post-content h4,
        .post-content h5 {
          scroll-margin-top: 70px;
        }
      }
    `;
        document.head.appendChild(style);

        // Cleanup
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Click handler for smooth scrolling with offset
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
        e.preventDefault();
        const element = document.getElementById(id);
        if (element) {
            // Smooth scroll to element
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const groupedHeadings = headings.reduce((groups: HeadingGroup[], heading: Heading) => {
        if (heading.level === "h2") {
            groups.push({
                mainHeading: heading,
                subHeadings: [],
            });
        } else if (groups.length > 0) {
            groups[groups.length - 1].subHeadings.push(heading);
        }
        return groups;
    }, []);

    const toggleSection = (headingId: string, e: React.MouseEvent) => {
        e.preventDefault();
        setExpandedSections(prev => ({
            ...prev,
            [headingId]: !prev[headingId]
        }));
    };

    return (
        <nav className="mb-8 rounded-md border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <button
                className="flex w-full items-center justify-between btn-drop-toc font-semibold text-gray-800 dark:text-slate-300 hover:underline focus:outline-none text-sm md:text-base lg:text-lg"
                onClick={() => setIsOpen(!isOpen)}
            >
                {isOpen ? t("title") : t("title")}
                <span className="ml-2">{isOpen ? "▲" : "▼"}</span>
            </button>

            {isOpen && (
                <ul className="mt-4 space-y-3">
                    {groupedHeadings.map(({ mainHeading, subHeadings }) => (
                        <li key={mainHeading.id} className="relative">
                            <div className="flex items-center gap-2">
                                <a
                                    href={`#${mainHeading.id}`}
                                    onClick={(e) => handleClick(e, mainHeading.id)}
                                    className="flex-grow text-gray-700 hover:text-blue-600 hover:underline dark:text-gray-300"
                                >
                                    {mainHeading.text}
                                </a>

                                {subHeadings.length > 0 && (
                                    <button
                                        onClick={(e) => toggleSection(mainHeading.id, e)}
                                        className="ml-2 flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100 focus:outline-none dark:hover:bg-gray-700"
                                        aria-label={expandedSections[mainHeading.id] ? "Collapse section" : "Expand section"}
                                    >
                                        <span className="text-gray-500">
                                            {expandedSections[mainHeading.id] ? "▼" : "▶"}
                                        </span>
                                    </button>
                                )}
                            </div>

                            {subHeadings.length > 0 && expandedSections[mainHeading.id] && (
                                <ul className="mt-2 space-y-2 border-l border-gray-200 pl-4">
                                    {subHeadings.map((subHeading) => (
                                        <li
                                            key={subHeading.id}
                                            className={`
                        ${subHeading.level === "h3" ? "ml-2" : ""}
                        ${subHeading.level === "h4" ? "ml-4" : ""}
                        ${subHeading.level === "h5" ? "ml-6" : ""}
                      `}
                                        >
                                            <a
                                                href={`#${subHeading.id}`}
                                                onClick={(e) => handleClick(e, subHeading.id)}
                                                className="block text-sm text-gray-600 hover:text-blue-600 hover:underline dark:text-gray-400"
                                            >
                                                {subHeading.text}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </nav>
    );
}
